import React from 'react';
import { PipelineItem, PipelineStatus, PipelineStep } from '../../../const/PipelineTypes';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Button, Tooltip } from '@dsi/react-eds';
import detailStyles from '../PipelineDetail.module.css';
import styles from './StagesView.module.css';
import { deduplicateSteps, getStageStatus } from '../PipelineUtils';
import StageToolTipIcon from './StageToolTipIcon';

interface StagesViewProps {
  predefinedStages: string[];
  stages: Record<string, PipelineStep[]>;
  pipeline: PipelineItem;
  expandedStages: Record<string, boolean>;
  theme: boolean;
  onToggleStage: (stageName: string) => void;
  onStepClick: (stageName: string, step: PipelineStep) => void;
}

const StagesView: React.FC<StagesViewProps> = ({
  predefinedStages,
  stages,
  pipeline,
  expandedStages,
  theme,
  onToggleStage,
  onStepClick,
}) => {
  return (
    <div className={styles.stagesContainer}>
      {predefinedStages.map((stageName: string, idx: number, arr: string[]) => {
        const steps = stages[stageName];
        const isExpanded = expandedStages[stageName];
        const stageStatus = getStageStatus(stageName, pipeline);

        const uniqueSteps = steps?.length ? deduplicateSteps(steps) : [];
        const stepCount = uniqueSteps.length;

        let duration = '';
        if (steps && steps.length > 0) {
          const sortedSteps = [...steps].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          const start = new Date(sortedSteps[0].timestamp).getTime();
          const end = new Date(sortedSteps[sortedSteps.length - 1].timestamp).getTime();
          if (!isNaN(start) && !isNaN(end) && end > start) {
            const diff = end - start;
            duration = `${Math.floor(diff / (1000 * 60))} m ${Math.floor((diff % (1000 * 60)) / 1000)} s`;
          }
        }

        return (
          <React.Fragment key={stageName}>
            <div className={styles.stageCard} onClick={() => onToggleStage(stageName)}>
              <div className={styles.stageCardHeader}>
                <div className={styles.stageCardHeaderLeft}>
                  <StageToolTipIcon status={stageStatus as PipelineStatus} size={18} marginBottom={6} theme={theme} />
                  {stageName}
                </div>
                <span className={styles.stageCardChevron}>
                  <i className={`icon ${isExpanded ? 'icon-chevron-up' : 'icon-chevron-down'} ${styles.stageChevron}`} />
                </span>
              </div>
              <div className={styles.stageCardSummary}>
                {stepCount} step{stepCount !== 1 ? 's' : ''} completed{duration ? `  ${duration}` : ''}
              </div>
              {isExpanded && (
                <div className={styles.stageCardExpanded}>
                  <ReactEDSWrapper component={Tooltip} message="Coming soon - stay tuned!" pointer>
                    <ReactEDSWrapper component={Button} disabled>
                      Rerun this stage
                    </ReactEDSWrapper>
                  </ReactEDSWrapper>

                  <div className={styles.stageDivider} />
                  <div>
                    {uniqueSteps.length > 0 &&
                      uniqueSteps.map((step: PipelineStep, stepIdx: number) => {
                          const allOccurrences = steps.filter((s) => s.Step.toLowerCase() === step.Step.toLowerCase());
                          let stepDuration = '';
                          if (allOccurrences.length > 1) {
                            const times = allOccurrences.map((s) => new Date(s.timestamp).getTime());
                            const minTime = Math.min(...times);
                            const maxTime = Math.max(...times);
                            if (!isNaN(minTime) && !isNaN(maxTime) && maxTime > minTime) {
                              const diff = maxTime - minTime;
                              stepDuration = `${Math.floor(diff / (1000 * 60))} m ${Math.floor((diff % (1000 * 60)) / 1000)} s`;
                            }
                          } else if (allOccurrences.length === 1) {
                            stepDuration = '0 m 0 s';
                          }
                          return (
                            <React.Fragment key={step.Step}>
                              <div
                                className={styles.stepRow}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onStepClick(stageName, step);
                                }}
                              >
                                <div className={styles.stepRowHeader}>
                                  <StageToolTipIcon status={step.Status.toLowerCase() as PipelineStatus} size={16} theme={theme} />
                                  <span className={detailStyles.truncateText}>{step.Step}</span>
                                </div>
                                {stepDuration !== '0 m 0 s' && (
                                  <span className={styles.stepDuration}>Step completed {stepDuration}</span>
                                )}
                              </div>
                              {stepIdx < uniqueSteps.length - 1 && <div className={styles.stepDivider} />}
                            </React.Fragment>
                          );
                        })}
                  </div>
                </div>
              )}
            </div>
            {idx < arr.length - 1 && (
              <div className={styles.connector}>
                <div className={styles.connectorLine} />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default StagesView;
